import {
	WorkfrontBundleObjectRole,
	WorkfrontMappingKind,
	WorkfrontMappingRule,
	WorkfrontSourceKind,
	WorkfrontTargetLevel,
} from '@atlas/types'
import { CardShell, SectionCard } from '@atlas/ui-components'
import { Button, Checkbox } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useFieldMappings } from '@/hooks/useWorkfrontMappings'
import type { FieldMappingListRow, FieldRow } from '@/services/workfront-mappings.service'
import { EnumFilter } from '../shared'
import { FieldsTable, fieldSourceName } from './components/FieldsTable'
import { useDrawerContext, useReportLoadFailure, useSelection } from './tab-support'
import { NEW_ROW_KEY, useFixLink, useMappingEditor } from './useMappingEditor'

/** A new field mapping reading `parameterId`, with everything else to choose. */
export const newFieldRow = (parameterId: string | null): FieldRow => ({
	sourceRole: WorkfrontBundleObjectRole.CHILD,
	sourceKind: WorkfrontSourceKind.PARAMETER,
	sourceParameterId: parameterId,
	sourceField: null,
	fallbackParameterId: null,
	targetPath: '',
	rule: WorkfrontMappingRule.DIRECT,
	ruleParams: {},
	enabled: true,
	notes: null,
})

interface Filters {
	role?: WorkfrontBundleObjectRole
	level?: WorkfrontTargetLevel
	rule?: WorkfrontMappingRule
	requiredOnly: boolean
}

const matches = (row: FieldMappingListRow, f: Filters) =>
	(!f.role || row.row.sourceRole === f.role) &&
	(!f.level || row.target?.level === f.level) &&
	(!f.rule || row.row.rule === f.rule) &&
	(!f.requiredOnly || !!row.target?.required)

/**
 * Fields: which Workfront value feeds which ATLAS target, by which rule. Rows open
 * in the drawer; several can be enabled or disabled at once. `?parameter=…&edit=1`
 * opens that field's mapping, or a new one reading it.
 */
export function FieldsTab() {
	const query = useFieldMappings()
	const rows = query.data ?? []
	const [filters, setFilters] = useState<Filters>({ requiredOnly: false })
	const selection = useSelection()
	const editor = useMappingEditor(WorkfrontMappingKind.FIELD, (key) => rows.find((r) => r.key === key)?.row)
	const context = useDrawerContext()
	useReportLoadFailure(query.isError, 'field mappings')

	const edit = (row: FieldMappingListRow) =>
		editor.open({
			key: row.key,
			row: row.row,
			title: `Edit ${fieldSourceName(row)}`,
			canDelete: row.status !== 'deleted',
		})
	useFixLink(query.isSuccess, (params) => {
		const parameterId = params.get('parameter')
		const existing = rows.find((r) => r.row.sourceParameterId === parameterId)
		const fromFix = params.get('source') !== 'unmapped'
		if (existing && fromFix)
			editor.open({ key: existing.key, row: existing.row, title: `Fix ${fieldSourceName(existing)}`, fromFix })
		else editor.open({ key: NEW_ROW_KEY, row: newFieldRow(parameterId), title: 'Map a Workfront field', fromFix })
	})

	const shown = rows.filter((row) => matches(row, filters))
	const bulk = (enabled: boolean) => {
		const saves = rows
			.filter((r) => selection.selected.has(r.key))
			.map((r) => ({ key: r.key, row: { ...r.row, enabled } }))
		editor.saveMany(saves, `Draft saved: ${saves.length} ${enabled ? 'enabled' : 'disabled'}`, selection.clear)
	}
	const set = (patch: Partial<Filters>) => setFilters({ ...filters, ...patch })

	return (
		<div className="space-y-4">
			<SectionCard title="Filters">
				<div className="grid items-end gap-4 md:grid-cols-4">
					<EnumFilter
						label="Role"
						values={Object.values(WorkfrontBundleObjectRole)}
						value={filters.role}
						onChange={(role) => set({ role })}
					/>
					<EnumFilter
						label="Target level"
						values={Object.values(WorkfrontTargetLevel)}
						value={filters.level}
						onChange={(level) => set({ level })}
					/>
					<EnumFilter
						label="Rule"
						values={Object.values(WorkfrontMappingRule)}
						value={filters.rule}
						onChange={(rule) => set({ rule })}
					/>
					<Checkbox isSelected={filters.requiredOnly} onChange={(requiredOnly) => set({ requiredOnly })}>
						Required targets only
					</Checkbox>
				</div>
			</SectionCard>
			<div className="flex flex-wrap gap-2">
				<Button
					size="sm"
					onPress={() => editor.open({ key: NEW_ROW_KEY, row: newFieldRow(null), title: 'Add a field mapping' })}
				>
					Add field mapping
				</Button>
				<Button
					size="sm"
					variant="outline"
					isDisabled={!selection.selected.size || editor.saving}
					onPress={() => bulk(true)}
				>
					Enable selected
				</Button>
				<Button
					size="sm"
					variant="outline"
					isDisabled={!selection.selected.size || editor.saving}
					onPress={() => bulk(false)}
				>
					Disable selected
				</Button>
			</div>
			<CardShell>
				<FieldsTable
					rows={shown}
					filtered={rows.length > 0}
					isLoading={query.isLoading}
					isError={query.isError}
					onRetry={() => void query.refetch()}
					selected={selection.selected}
					onToggle={selection.onToggle}
					onEdit={edit}
				/>
			</CardShell>
			{editor.drawer(context)}
		</div>
	)
}
